import { useEffect, useMemo, useState } from "react";
import { Card } from "@component/card";
import { Button } from "@component/button";
import { Progress } from "@component/progress";
import {
    Loader2Icon,
    XIcon,
    CheckCircle2Icon,
    AlertCircleIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    FileTextIcon,
    ChevronRightIcon,
    FileXIcon,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { useBulkUploadCVContext, BulkUploadFileTracker, clearBulkUploadState, saveBulkUploadState } from "@context/bulk_upload_cv_context";
import { Tooltip } from "@component/tooltip";
import { useNavigate } from "react-router-dom";
import { useUserProfileContext } from "@context/user_profile";
import { useBulkUploadProgress } from "@hook/use-bulk-upload-progress";
import { BulkUploadWSProgressMessage } from "@type/cv";
import { cancelBulkUploadCV } from "@service/cv";
import CancelUploadConfirmationDialog from "@container/dialog/dialog-confirm-cancel-upload";

type FileStatus = "processing" | "success" | "failed";

interface FileTracker {
    temp_cv_id: string;
    filename: string;
    status: FileStatus;
    message?: string;
    errorType?: "system_error" | "not_cv" | "duplicate" | "cancelled";
}

export default function CVProcessingToast() {
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const [, userInfo] = useUserProfileContext();
    const {
        jobId,
        fileTrackerMap,
        setFileTrackerMap,
        batchId,
        setBatchId,
        isVisible,
        setIsVisible,
        setIsProcessing,
        setReloadCount,
    } = useBulkUploadCVContext();

    const apiBaseUrl = import.meta.env.VITE_TALENT_AI_BE_BASE_URL || "";
    const userId = userInfo?.id || "";

    const {
        latestMessage,
        connect: wsConnect,
        disconnect: wsDisconnect,
    } = useBulkUploadProgress(userId, apiBaseUrl);

    const [fileTrackers, setFileTrackers] = useState<FileTracker[]>([]);
    const [showBlueBorder, setShowBlueBorder] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSystemErrorExpanded, setIsSystemErrorExpanded] = useState(true);
    const [isNotCVExpanded, setIsNotCVExpanded] = useState(true);
    const [isDone, setIsDone] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const systemErrorFiles = useMemo(() => fileTrackers.filter(f => f.status === "failed" && f.errorType === "system_error"), [fileTrackers]);
    const notCVFiles = useMemo(() => fileTrackers.filter(f => f.status === "failed" && f.errorType === "not_cv"), [fileTrackers]);
    const nonFailedFiles = useMemo(() => fileTrackers.filter(f => f.status !== "failed"), [fileTrackers]);

    const completedFiles = fileTrackers.filter(f => f.status === "success").length;
    const failedFiles = fileTrackers.filter(f => f.status === "failed").length;
    const processingCount = fileTrackers.filter(f => f.status === "processing").length;
    const totalFiles = fileTrackers.length;
    const isAllCompleted = totalFiles > 0 && processingCount === 0 && isDone;
    const hasFailures = failedFiles > 0;
    const showConsolidatedView = totalFiles >= 20;
    const overallProgress = totalFiles > 0 ? Math.min(100, Math.round(((completedFiles + failedFiles) / totalFiles) * 100)) : 0;

    // Initialize file trackers from fileTrackerMap (temp_cv_id → filename mapping from API response or localStorage)
    useEffect(() => {
        if (fileTrackerMap.length === 0) return;

        const hasAnyStatus = fileTrackerMap.some((t: BulkUploadFileTracker) => t.status && t.status !== "processing");

        setFileTrackers(fileTrackerMap.map((t: BulkUploadFileTracker) => ({
            temp_cv_id: t.temp_cv_id,
            filename: t.filename,
            status: (t.status || "processing") as FileStatus,
            message: t.message,
        })));
        setIsDone(false);

        // Only show blue border animation for fresh uploads (not restored from localStorage)
        if (!hasAnyStatus) {
            setShowBlueBorder(true);
            setTimeout(() => setShowBlueBorder(false), 2000);
        }
        setFileTrackerMap([]);
    }, [fileTrackerMap]);

    // Connect WebSocket when batchId is set (queue API succeeded)
    useEffect(() => {
        if (!batchId || !isVisible || !userId) return;

        wsConnect();

        return () => {
            wsDisconnect();
        };
    }, [batchId, isVisible, userId, wsConnect, wsDisconnect]);

    // Handle WebSocket progress messages (matched by temp_cv_id)
    useEffect(() => {
        if (!latestMessage) return;

        const msg: BulkUploadWSProgressMessage = latestMessage;
        const isTerminal = msg.event === "completed" || msg.event === "cancelled";

        setFileTrackers(prev => {
            const existing = prev.find(t => t.temp_cv_id === msg.temp_cv_id);
            // All non-success statuses (failed, duplicate, cancelled) are mapped to "failed" for FileTracker.status.
            // The granular error reason is stored in errorType, which drives UI categorization (sections, badges, tooltips).
            const newStatus: FileStatus = msg.status === "success" ? "success" : "failed";
            const getErrorType = (): FileTracker["errorType"] => {
                if (msg.status === "cancelled") return "cancelled";
                if (msg.status === "duplicate") return "duplicate";
                if (msg.message?.toLowerCase().includes("not a valid cv")) return "not_cv";
                if (msg.status === "failed") return "system_error";
                return undefined;
            };
            const errorType = getErrorType();

            let updated: FileTracker[];
            if (existing) {
                updated = prev.map(t =>
                    t.temp_cv_id === msg.temp_cv_id
                        ? { ...t, status: newStatus, message: msg.message, errorType }
                        : t
                );
            } else {
                // Unknown temp_cv_id from server — add as new entry
                updated = [...prev, {
                    temp_cv_id: msg.temp_cv_id,
                    filename: msg.temp_cv_id,
                    status: newStatus,
                    message: msg.message,
                    errorType,
                }];
            }

            // Persist progress to localStorage — but NOT on the terminal event.
            // This updater runs during React's commit phase, i.e. AFTER the
            // synchronous clearBulkUploadState() below. Saving here on the
            // terminal message would re-write the completed state right after
            // we cleared it, leaving a stuck toast that reappears on refresh.
            if (jobId && batchId && !isTerminal) {
                saveBulkUploadState({
                    jobId,
                    batchId,
                    fileTrackerMap: updated.map(t => ({
                        temp_cv_id: t.temp_cv_id,
                        filename: t.filename,
                        status: t.status,
                        message: t.message,
                    })),
                });
            }

            return updated;
        });

        if (isTerminal) {
            setIsDone(true);
            setIsProcessing(false);
            wsDisconnect();
            clearBulkUploadState();
        }
    }, [latestMessage]);

    // Trigger reload when all done
    useEffect(() => {
        if (isAllCompleted) {
            setReloadCount(prev => prev + 1);
        }
    }, [isAllCompleted]);

    // Auto-close when there is nothing left to show: no active file trackers AND
    // nothing pending to initialize from the queue response / restored state.
    // Covers (1) every tracked file being removed/deleted during the session and
    // (2) a refresh that lands on an empty state — both would otherwise leave a
    // stuck "Processing 0 items" card. The `fileTrackerMap.length === 0` guard
    // prevents closing in the brief frame before a fresh upload's trackers init.
    useEffect(() => {
        if (isVisible && fileTrackers.length === 0 && fileTrackerMap.length === 0) {
            setIsVisible(false);
            setIsProcessing(false);
            setBatchId(null);
            setIsDone(false);
            clearBulkUploadState();
        }
    }, [isVisible, fileTrackers.length, fileTrackerMap.length]);

    const onClose = () => {
        setIsVisible(false);
        clearBulkUploadState();
        if (isDone) {
            setFileTrackers([]);
            setBatchId(null);
            setIsDone(false);
        }
    };

    const onCancelClick = () => {
        setShowCancelConfirm(true);
    };

    const onCancelConfirm = async () => {
        if (!batchId) return;
        setIsCancelling(true);
        try {
            await cancelBulkUploadCV(batchId);
        } catch {
            // proceed with local cleanup even if API fails
        } finally {
            wsDisconnect();
            setIsVisible(false);
            setIsProcessing(false);
            setFileTrackers([]);
            setBatchId(null);
            setFileTrackerMap([]);
            setIsDone(false);
            setShowCancelConfirm(false);
            setIsCancelling(false);
            clearBulkUploadState();
        }
    };

    const onViewDetails = () => {
        if (!jobId) return;
        if (params.get("tab") === "uploads") {
            setReloadCount(prev => prev + 1);
        } else {
            navigate(`/jobs/${jobId}/applications?tab=uploads`);
        }
    };

    const onRemove = (tempCvId: string) => {
        setFileTrackers(prev => prev.filter(f => f.temp_cv_id !== tempCvId));
    };

    // Only show the progress toast to an authenticated user. This component is
    // mounted at the app root (outside <Routes>), so without this guard a
    // persisted upload state would render the toast on public/guest routes such
    // as the login page. `userInfo` is null until the profile loads / when
    // logged out, which is exactly when the toast must stay hidden.
    if (!userInfo || !isVisible) return null;

    const headerLabel = (() => {
        if (isAllCompleted) return "Processing Complete";
        if (hasFailures && processingCount === 0 && isDone) return `${failedFiles} Failed`;
        return `Processing ${processingCount} item${processingCount !== 1 ? "s" : ""}`;
    })();

    const headerIcon = (() => {
        if (isAllCompleted) return <CheckCircle2Icon className="w-5 h-5 text-green-600 dark:text-green-400" />;
        if (hasFailures && processingCount === 0 && isDone) return <AlertCircleIcon className="w-5 h-5 text-destructive" />;
        return <Loader2Icon className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />;
    })();

    return (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 duration-300">
            <Card className={cn(
                "w-[480px] shadow-xl border-border bg-background transition-all duration-300",
                showBlueBorder && "ring-2 ring-blue-500 ring-offset-1"
            )}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
                    <div className="flex items-center gap-2">
                        {headerIcon}
                        <span className="text-sm font-semibold text-foreground">
                            {headerLabel}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? (
                                <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                                <ChevronUpIcon className="w-4 h-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted"
                            onClick={onClose}
                        >
                            <XIcon className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                    <div className="flex flex-col">
                        {showConsolidatedView ? (
                            /* Consolidated View for 20+ files */
                            <>
                                {/* Overall Progress Bar */}
                                <div className="px-4 py-4 space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">
                                            {completedFiles + failedFiles} of {totalFiles} files
                                        </span>
                                        <span className="font-medium text-foreground">
                                            {overallProgress}%
                                        </span>
                                    </div>
                                    <Progress
                                        value={overallProgress}
                                        className="h-2 [&>div]:bg-blue-600 dark:[&>div]:bg-blue-400"
                                    />
                                </div>

                                {/* Status / Cancel / View Details */}
                                {processingCount > 0 ? (
                                    <div className="flex items-center justify-between px-4 py-2 pt-2 border-t border-border">
                                        <span className="text-sm text-muted-foreground">
                                            {processingCount} file{processingCount !== 1 ? "s" : ""} remaining
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                            onClick={onCancelClick}
                                        >
                                            CANCEL
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between px-4 py-2 border-t border-border">
                                        <span className="text-sm text-muted-foreground">
                                            {totalFiles} files processed ({completedFiles} success, {failedFiles} failed)
                                        </span>
                                        {failedFiles !== totalFiles && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                                onClick={onViewDetails}
                                            >
                                                VIEW DETAILS
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {/* Failed Files List */}
                                <div className="max-h-80 overflow-y-auto">
                                    {failedFiles > 0 && (
                                        <div className="px-4 py-2 bg-muted/30 border-t border-border">
                                            <div className="text-xs font-medium text-muted-foreground">
                                                Failed Uploads
                                            </div>
                                        </div>
                                    )}
                                    {/* System Error Section */}
                                    {systemErrorFiles.length > 0 && (
                                        <div>
                                            <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                                                <button
                                                    onClick={() => setIsSystemErrorExpanded(!isSystemErrorExpanded)}
                                                    className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                                                >
                                                    {isSystemErrorExpanded ? (
                                                        <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                    <AlertCircleIcon className="w-4 h-4 text-amber-500" />
                                                    <span className="text-xs font-medium text-foreground">
                                                        System Error ({systemErrorFiles.length})
                                                    </span>
                                                </button>
                                            </div>
                                            {isSystemErrorExpanded && (
                                                <div className="divide-y divide-border">
                                                    {systemErrorFiles.map((file) => (
                                                        <FileItem key={file.temp_cv_id} file={file} onRemove={onRemove} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Not a CV Section */}
                                    {notCVFiles.length > 0 && (
                                        <div>
                                            <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                                                <button
                                                    onClick={() => setIsNotCVExpanded(!isNotCVExpanded)}
                                                    className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                                                >
                                                    {isNotCVExpanded ? (
                                                        <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                    <FileXIcon className="w-4 h-4 text-destructive" />
                                                    <span className="text-xs font-medium text-foreground">
                                                        Not a CV ({notCVFiles.length})
                                                    </span>
                                                </button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                                    onClick={() => notCVFiles.forEach(file => onRemove(file.temp_cv_id))}
                                                >
                                                    REMOVE ALL
                                                </Button>
                                            </div>
                                            {isNotCVExpanded && (
                                                <div className="divide-y divide-border">
                                                    {notCVFiles.map((file) => (
                                                        <FileItem key={file.temp_cv_id} file={file} onRemove={onRemove} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <ErrorSection
                                        icon={<AlertCircleIcon className="w-4 h-4 text-amber-500" />}
                                        label="Duplicate"
                                        files={fileTrackers.filter(f => f.status === "failed" && f.errorType === "duplicate")}
                                        onRemove={onRemove}
                                    />
                                    <ErrorSection
                                        icon={<XIcon className="w-4 h-4 text-muted-foreground" />}
                                        label="Cancelled"
                                        files={fileTrackers.filter(f => f.status === "failed" && f.errorType === "cancelled")}
                                        onRemove={onRemove}
                                    />
                                </div>
                            </>
                        ) : (
                            /* Individual File View for < 20 files */
                            <>
                                {/* Status / Cancel / View Details */}
                                <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                                    {processingCount > 0 ? (
                                        <>
                                            <span className="text-sm text-muted-foreground">
                                                {processingCount} file{processingCount !== 1 ? "s" : ""} remaining
                                            </span>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                                onClick={onCancelClick}
                                            >
                                                CANCEL
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-sm text-muted-foreground">
                                                {isAllCompleted
                                                    ? `${totalFiles} file${totalFiles !== 1 ? "s" : ""} processed`
                                                    : hasFailures
                                                        ? "Processing complete with errors."
                                                        : "Ready to review"}
                                            </span>
                                            {failedFiles !== totalFiles && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                                    onClick={onViewDetails}
                                                >
                                                    VIEW DETAILS
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="max-h-80 overflow-y-auto">
                                    {/* Non-failed files */}
                                    {nonFailedFiles.length > 0 && (
                                        <div className="divide-y divide-border">
                                            {nonFailedFiles.map((file) => (
                                                <FileItem key={file.temp_cv_id} file={file} onRemove={onRemove} />
                                            ))}
                                        </div>
                                    )}

                                    {/* Failed Uploads Header */}
                                    {failedFiles > 0 && (
                                        <div className="px-4 py-2 bg-muted/30 border-y border-border">
                                            <div className="text-xs font-medium text-muted-foreground">
                                                Failed Uploads
                                            </div>
                                        </div>
                                    )}

                                    {/* System Error Section */}
                                    {systemErrorFiles.length > 0 && (
                                        <div>
                                            <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                                                <button
                                                    onClick={() => setIsSystemErrorExpanded(!isSystemErrorExpanded)}
                                                    className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                                                >
                                                    {isSystemErrorExpanded ? (
                                                        <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                    <AlertCircleIcon className="w-4 h-4 text-amber-500" />
                                                    <span className="text-xs font-medium text-foreground">
                                                        System Error ({systemErrorFiles.length})
                                                    </span>
                                                </button>
                                            </div>
                                            {isSystemErrorExpanded && (
                                                <div className="divide-y divide-border">
                                                    {systemErrorFiles.map((file) => (
                                                        <FileItem key={file.temp_cv_id} file={file} onRemove={onRemove} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Not a CV Section */}
                                    {notCVFiles.length > 0 && (
                                        <div>
                                            <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                                                <button
                                                    onClick={() => setIsNotCVExpanded(!isNotCVExpanded)}
                                                    className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                                                >
                                                    {isNotCVExpanded ? (
                                                        <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                    <FileXIcon className="w-4 h-4 text-destructive" />
                                                    <span className="text-xs font-medium text-foreground">
                                                        Not a CV ({notCVFiles.length})
                                                    </span>
                                                </button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                                    onClick={() => notCVFiles.forEach(file => onRemove(file.temp_cv_id))}
                                                >
                                                    REMOVE ALL
                                                </Button>
                                            </div>
                                            {isNotCVExpanded && (
                                                <div className="divide-y divide-border">
                                                    {notCVFiles.map((file) => (
                                                        <FileItem key={file.temp_cv_id} file={file} onRemove={onRemove} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <ErrorSection
                                        icon={<AlertCircleIcon className="w-4 h-4 text-amber-500" />}
                                        label="Duplicate"
                                        files={fileTrackers.filter(f => f.status === "failed" && f.errorType === "duplicate")}
                                        onRemove={onRemove}
                                    />
                                    <ErrorSection
                                        icon={<XIcon className="w-4 h-4 text-muted-foreground" />}
                                        label="Cancelled"
                                        files={fileTrackers.filter(f => f.status === "failed" && f.errorType === "cancelled")}
                                        onRemove={onRemove}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Card>

            <CancelUploadConfirmationDialog
                isOpen={showCancelConfirm}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={onCancelConfirm}
                isLoading={isCancelling}
            />
        </div>
    );
}

interface ErrorSectionProps {
    icon: React.ReactNode;
    label: string;
    files: FileTracker[];
    onRemove: (tempCvId: string) => void;
}

function ErrorSection({ icon, label, files, onRemove }: ErrorSectionProps) {
    if (files.length === 0) return null;
    return (
        <div>
            <div className="flex items-center gap-2 px-4 py-2 bg-background border-y border-border">
                {icon}
                <span className="text-xs font-medium text-foreground">
                    {label} ({files.length})
                </span>
            </div>
            <div className="divide-y divide-border">
                {files.map((file) => (
                    <FileItem key={file.temp_cv_id} file={file} onRemove={onRemove} />
                ))}
            </div>
        </div>
    );
}

interface FileItemProps {
    file: FileTracker;
    onRemove: (tempCvId: string) => void;
}

function FileItem({ file, onRemove }: FileItemProps) {
    const isInProgress = file.status === "processing";
    const isCompleted = file.status === "success";
    const isFailed = file.status === "failed";

    return (
        <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-all duration-150 overflow-hidden">
            {/* File Icon */}
            <FileTextIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />

            {/* File Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate block min-w-0" title={file.filename}>
                        {file.filename}
                    </span>
                    {isFailed && (
                        <Tooltip
                            content={(
                                <p className="text-xs">
                                    {file.message
                                        || (file.errorType === "duplicate" ? "Candidate already applied to this job."
                                            : file.errorType === "cancelled" ? "Upload was cancelled."
                                                : file.errorType === "not_cv" ? "File is not a valid CV."
                                                    : "Failed to process")}
                                </p>
                            )}
                        >
                            <span className={cn(
                                "text-xs flex-shrink-0 px-2 py-0.5 rounded-full whitespace-nowrap cursor-help transition-colors",
                                file.errorType === "duplicate"
                                    ? "text-amber-700 bg-amber-100 hover:bg-amber-200 dark:text-amber-300 dark:bg-amber-900/30 dark:hover:bg-amber-900/50"
                                    : file.errorType === "cancelled"
                                        ? "text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700"
                                        : "text-destructive bg-destructive/10 hover:bg-destructive/20"
                            )}>
                                {file.errorType === "duplicate" ? "Duplicate" : file.errorType === "cancelled" ? "Cancelled" : "Failed"}
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Status Icon / Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
                {isCompleted && (
                    <CheckCircle2Icon className="w-5 h-5 text-green-600 dark:text-green-400" />
                )}

                {isInProgress && (
                    <Loader2Icon className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                )}

                {isFailed && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-muted"
                        onClick={() => onRemove(file.temp_cv_id)}
                        title="Remove file"
                    >
                        <XIcon className="w-4 h-4 text-destructive" />
                    </Button>
                )}
            </div>
        </div>
    );
}
